import React from 'react';
import { FlexContainer } from 'components/Containers/FlexContainer/FlexContainer';
import Label from 'components/form/Label/Label';
import { formatAsFull, monthsToYears } from 'helpers/dateHelper';
import classnames from 'classnames';

import { ReactComponent as ArrowDownGreen } from 'style/_assets/icons/arrow-down-green.svg';
import { ReactComponent as ArrowDownRed } from 'style/_assets/icons/arrow-down-red.svg';
import { ReactComponent as ArrowUpGreen } from 'style/_assets/icons/arrow-up-green.svg';
import { ReactComponent as ArrowUpRed } from 'style/_assets/icons/arrow-up-red.svg';
import { ConditionalRendering } from 'helpers/conditionalRedering.helper';
import { commafy } from 'helpers/number';

const Info = ({ label, value, isBold, arrowColor, ArrowIcon, arrowValue }) => {
  return (
    <div className="margb16">
      <Label className="margb4 fontNeutral300" type="caption">
        {label}
      </Label>
      <FlexContainer alignItemsCenter>
        <Label
          className={classnames({
            bold: isBold,
          })}
          type="Body-1"
        >
          {value}
        </Label>
        <div className="padl4 height24px">{ArrowIcon}</div>

        <ConditionalRendering isTrue={arrowValue}>
          <span className={arrowColor}>{arrowValue}</span>
        </ConditionalRendering>
      </FlexContainer>
    </div>
  );
};

const valueChanged = (current, previous, isReverse = false) => {
  if (current > previous) {
    return isReverse ? <ArrowUpRed /> : <ArrowUpGreen />;
  }
  if (current < previous) {
    return isReverse ? <ArrowDownGreen /> : <ArrowDownRed />;
  }

  return <></>;
};

const valueColor = (current, previous, isReverse) => {
  if (current >= previous) {
    return isReverse ? 'fontRed100' : 'fontGreen300';
  }
  if (current < previous) {
    return isReverse ? 'fontGreen300' : 'fontRed100';
  }
};
const ScoreBandChange = props => {
  const {
    tradelines: { current, previous },
  } = props;

  const creditScoreArrow = valueChanged(
    current?.creditScore,
    previous?.creditScore,
  );

  const arrowValueCreditScore = Math.abs(
    current?.creditScore - previous?.creditScore,
  );
  const arrowValueColor = valueColor(
    current?.creditScore,
    previous?.creditScore,
  );

  const negativeMarksArrow = valueChanged(
    current?.negativeMarks,
    previous?.negativeMarks,
    true,
  );
  const creditUsageArrow = valueChanged(
    current?.creditUsage,
    previous?.creditUsage,
    true,
  );
  const totalCreditLimitArrow = valueChanged(
    current?.totalCreditLimit,
    previous?.totalCreditLimit,
  );
  const creditAgeInMonthsArrow = valueChanged(
    current?.creditAgeInMonths,
    previous?.creditAgeInMonths,
  );
  const creditInquiriesArrow = valueChanged(
    current?.creditInquiries,
    previous?.creditInquiries,
    true,
  );

  return (
    <FlexContainer>
      <FlexContainer className="format50" column>
        <Label type="Subtitle-2">Previous report</Label>
        <Label className="fontNeutral300" type="caption">
          {formatAsFull(previous?.scoreDate)}
        </Label>
        <Label className="margb8" type="H2" typeHeader={'h2'}>
          {previous?.creditScore}
        </Label>
        <Info label="Negative marks" value={previous?.negativeMarks} />
        <Info label="Credit usage" value={`${previous?.creditUsage ?? 0}%`} />
        <Info
          label="Total credit limit"
          value={`$${commafy(previous?.totalCreditLimit)}`}
        />
        <Info
          label="Credit age"
          value={monthsToYears(previous?.creditAgeInMonths)}
        />
        <Info label="Credit inquiries" value={previous?.creditInquiries} />
      </FlexContainer>
      <FlexContainer column>
        <Label type="Subtitle-2">Current report</Label>
        <Label className="fontNeutral300" type="caption">
          {formatAsFull(current?.scoreDate)}
        </Label>
        <FlexContainer alignItemsCenter>
          <Label className="margb8" type="H2" typeHeader={'h2'}>
            {current?.creditScore}
          </Label>
          <div className="padl4 height24px">{creditScoreArrow}</div>

          <ConditionalRendering isTrue={arrowValueCreditScore}>
            <span className={arrowValueColor}>{arrowValueCreditScore}</span>
          </ConditionalRendering>
        </FlexContainer>

        <Info
          label="Negative marks"
          value={current?.negativeMarks}
          ArrowIcon={negativeMarksArrow}
          arrowColor={valueColor(
            current?.negativeMarks,
            previous?.negativeMarks,
            true,
          )}
          arrowValue={Math.abs(
            current?.negativeMarks - previous?.negativeMarks,
          )}
        />
        <Info
          label="Credit usage"
          value={`${current?.creditUsage ?? 0}%`}
          ArrowIcon={creditUsageArrow}
          arrowColor={valueColor(
            current?.creditUsage,
            previous?.creditUsage,
            true,
          )}
          arrowValue={Math.abs(current?.creditUsage - previous?.creditUsage)}
        />
        <Info
          label="Total credit limit"
          value={`$${commafy(current?.totalCreditLimit)}`}
          ArrowIcon={totalCreditLimitArrow}
          arrowColor={valueColor(
            current?.totalCreditLimit,
            previous?.totalCreditLimit,
          )}
          arrowValue={Math.abs(
            current?.totalCreditLimit - previous?.totalCreditLimit,
          )}
        />
        <Info
          label="Credit age"
          value={monthsToYears(current?.creditAgeInMonths)}
          ArrowIcon={creditAgeInMonthsArrow}
          arrowColor={valueColor(
            current?.creditAgeInMonths,
            previous?.creditAgeInMonths,
          )}
          arrowValue={Math.abs(
            current?.creditAgeInMonths - previous?.creditAgeInMonths,
          )}
        />
        <Info
          label="Credit inquiries"
          value={current?.creditInquiries}
          ArrowIcon={creditInquiriesArrow}
          arrowColor={valueColor(
            current?.creditInquiries,
            previous?.creditInquiries,
            true,
          )}
          arrowValue={Math.abs(
            current?.creditInquiries - previous?.creditInquiries,
          )}
        />
      </FlexContainer>
    </FlexContainer>
  );
};

export default ScoreBandChange;
